import { ArrowRight, BookmarkPlus, Check, Clapperboard, Crosshair, Eraser, Info, Loader2, MessageSquareQuote, Search, Type, Upload, Youtube } from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { addBacklog, createJob, createLocate, createScan, getSettings, listMusicTracks, uploadVideo, type BacklogDraft, type ChannelFeatures, type MusicTrack } from "../api";
import { MusicPicker } from "../components/MusicPicker";
import { SamplePicker } from "../components/SamplePicker";
import { PageHeader } from "../components/PageHeader";
import { cardStyle, ctaBtnStyle, fieldLabelStyle, inputStyle, monoInputStyle } from "../components/styles";
import { validateTimestamp, validateYouTubeUrl } from "../lib/validate";
import { SCAN_DEFAULT_COUNT, SCAN_MAX_COUNT, SCAN_PRESET_COUNTS } from "../lib/scanConfig";
import { clearDraft, draftHasContent, loadDraft, saveDraft, type NewJobDraft } from "../lib/newJobDraft";
import { useIsMobile } from "../lib/useIsMobile";

interface Props {
  onCreated: (id: string) => void;
  /** Pre-fill values from a Backlog row. The form consumes this once on
   * mount and immediately tells App to clear it via onConsumePrefill so
   * navigating away and back doesn't keep restoring stale data. */
  prefill?: BacklogDraft | null;
  onConsumePrefill?: () => void;
  /** Pre-fill "Find a scene" mode (URL + opening/closing) — used to re-edit a failed locate. */
  locatePrefill?: { url: string; openingLine: string; closingLine: string } | null;
  onConsumeLocatePrefill?: () => void;
  /** Fired after a successful "Save as draft" so the parent can route
   *  to the Backlog screen. Falls back to a local "Saved" pulse when
   *  not provided. */
  onSavedToBacklog?: () => void;
  /** Active channel features — gates the Auto-scan toggle. Absent = all toggles hidden. */
  features?: ChannelFeatures;
  /** Active channel's caption language display name (e.g. "Arabic", "French").
   *  Drives the header + caption-toggle copy. Absent → "Arabic" (V1 default). */
  captionLanguage?: string;
  /** Called immediately after createScan returns — routes to Backlog without delay.
   *  Distinct from onSavedToBacklog (which uses a 1800ms timeout for draft saves). */
  onScanComplete?: (scanId?: string) => void;
  /** Which front door to open on. Set by the post-Brand-Setup "try a video"
   *  handoff ("sample") and by "Try another sample" on a failed first video.
   *  Re-applied whenever the VALUE changes, so a parent can steer an
   *  already-mounted form; the operator's own toggling is never fought. */
  initialMode?: SourceMode;
  /** This is the user's very first video. Adds a one-line lead above the
   *  sample picker; the job it creates gets the first-video treatment on the
   *  job screen (App owns that half). */
  firstVideo?: boolean;
}

type EndMode = "end" | "duration";

/** The three front doors. `youtube` is the classic form, `upload` a local
 *  9:16 clip, `sample` a curated clip already on the box. */
export type SourceMode = "youtube" | "upload" | "sample";

/** "1:30" or "90" → 90 seconds. Returns NaN for unparseable input. */
function parseDuration(input: string): number {
  const trimmed = input.trim();
  if (!trimmed) return NaN;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const parts = trimmed.split(":").map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n))) return NaN;
  if (parts.length === 2) return parts[0]! * 60 + parts[1]!;
  if (parts.length === 3) return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  return NaN;
}

export function NewJob({ onCreated, prefill, onConsumePrefill, locatePrefill, onConsumeLocatePrefill, onSavedToBacklog, features, captionLanguage = "Arabic", onScanComplete, initialMode, firstVideo }: Props): React.JSX.Element {
  // Restore an autosaved draft on mount so navigating away (or a refresh)
  // doesn't lose typed work. Skip restore when we arrived via a prefill
  // (Backlog/Sources "Use in New Job") or a locate prefill — those are
  // deliberate "load this exact thing" actions that should define the form
  // rather than fight a stale draft. Read once (deps []) so it never re-runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restored = useMemo(() => (prefill || locatePrefill ? null : loadDraft()), []);
  const hadDraft = restored != null;
  // Scan/locate modes only exist when the active channel exposes autoScan.
  // Restoring them on a channel without it would hide the Start fields with
  // no toggle to undo it — so gate the restore on the feature.
  const canScan = !!features?.autoScan;
  const isMobile = useIsMobile();

  const [url, setUrl] = useState(restored?.url ?? "");
  const [from, setFrom] = useState(restored?.from ?? "");
  const [endMode, setEndMode] = useState<EndMode>(restored?.endMode === "duration" ? "duration" : "end");
  const [to, setTo] = useState(restored?.to ?? "");
  const [duration, setDuration] = useState(restored?.duration ?? "");
  const [startPhrase, setStartPhrase] = useState(restored?.startPhrase ?? "");
  const [endPhrase, setEndPhrase] = useState(restored?.endPhrase ?? "");
  const [host, setHost] = useState(restored?.host ?? "");
  const [guests, setGuests] = useState(restored?.guests ?? "");
  const [topic, setTopic] = useState(restored?.topic ?? "");
  const [autoCaption, setAutoCaption] = useState(restored?.autoCaption ?? true);
  const [autoSocial, setAutoSocial] = useState(restored?.autoSocial ?? true);
  // Music picker — operator can pre-choose the background track. The
  // default is a random pick from the catalog, fixed at fetch-time so
  // the choice doesn't keep changing under the operator while they
  // adjust other fields. They can shuffle to re-roll or pick by name.
  // An empty string means "let Claude pick" (legacy behaviour) —
  // surfaced as the "Let Claude pick" radio option so it's visible
  // rather than hidden in form-init logic.
  const [musicTracks, setMusicTracks] = useState<MusicTrack[] | null>(null);
  const [musicTrack, setMusicTrack] = useState<string>(restored?.musicTrack ?? "");
  // Did the operator DELIBERATELY choose music this session (clicked a track or
  // "Let Claude pick"), vs. just inheriting the random on-mount suggestion? Only
  // a deliberate choice carries onto scan/locate candidates — an untouched form
  // leaves scanned scenes on per-scene Claude picks (today's behavior). The
  // random seed and a restored draft do NOT count (a draft can hold a stale
  // random seed), so scans stay on Auto until the operator actually picks.
  // Direct render is unaffected — it honors the shown track as before.
  const [musicTouched, setMusicTouched] = useState(false);
  const [musicError, setMusicError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedPulse, setSavedPulse] = useState(false);
  // ---------- auto-scan mode (gated behind features?.autoScan) ----------
  const [autoScanMode, setAutoScanMode] = useState(canScan ? (restored?.autoScanMode ?? false) : false);
  // ---------- locate mode ("find a scene I know") ----------
  const [locateMode, setLocateMode] = useState(canScan ? (restored?.locateMode ?? false) : false);
  const [openingLine, setOpeningLine] = useState(restored?.openingLine ?? "");
  const [closingLine, setClosingLine] = useState(restored?.closingLine ?? "");
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanCount, setScanCount] = useState<number>(
    typeof restored?.scanCount === "number"
      ? Math.max(1, Math.min(SCAN_MAX_COUNT, restored.scanCount))
      : SCAN_DEFAULT_COUNT,
  );
  // ---------- upload mode (third source: an already-9:16 local clip) ----------
  // sourceMode is intentionally NOT restored from the autosave draft and NOT
  // added to the persisted draft shape: an upload draft is useless without the
  // File object, and a File cannot be serialized to localStorage. It always
  // defaults to 'youtube' on every load so YouTube mode stays byte-identical.
  // Sample mode is the exception to the "always youtube on load" rule: it is
  // never restored from a draft either, but a PARENT may open the form on it
  // (the post-Brand-Setup handoff), which is an explicit navigation, not
  // restored state.
  const [sourceMode, setSourceMode] = useState<SourceMode>(initialMode ?? "youtube");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPct, setUploadPct] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-field validation errors keyed by field name. Cleared on
  // edit so a fixed field stops shouting before the next submit.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // One-time confirmation banner after a Backlog "Use in New Job"
  // click successfully populates the form. Without this, the user
  // can't tell whether the click did anything (the page swap is
  // instant and the fields filling can be missed).
  const [prefilledBanner, setPrefilledBanner] = useState(false);
  // QL_SAMPLES (server: GET /api/settings.features.samples). An older server,
  // or a blip, reads as ON — the same convention App uses for brandStudy —
  // because an empty catalog already renders honestly as "No samples yet".
  const [samplesOn, setSamplesOn] = useState(true);

  useEffect(() => {
    void getSettings()
      .then((s) => setSamplesOn(s.features?.samples !== false))
      .catch(() => { /* older server / blip — keep the default */ });
  }, []);

  // A parent steering the front door (the post-Brand-Setup handoff, "Try
  // another sample"). Keyed on the VALUE, so it fires once per change and
  // never fights the operator's own toggling afterwards.
  useEffect(() => {
    if (initialMode) setSourceMode(initialMode);
  }, [initialMode]);

  // Samples turned off server-side while the form sits on them (or a stale
  // handoff): fall back to the classic form rather than an empty screen — and
  // SAY SO. Silently swapping the screen under someone who just clicked "Try a
  // sample video" is the worst version of this (H5).
  const [samplesOffNotice, setSamplesOffNotice] = useState(false);
  useEffect(() => {
    if (!samplesOn && sourceMode === "sample") {
      setSourceMode("youtube");
      setSamplesOffNotice(true);
    }
  }, [samplesOn, sourceMode]);

  const clearFieldError = (key: string): void => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });
  };

  // Hydrate from a Backlog "Use in New Job" pre-fill exactly once. The
  // duration field comes through as a number (seconds); the form treats
  // it as a string (the operator can type "1:30" or "90"). Convert.
  useEffect(() => {
    if (!locatePrefill) return;
    if (locatePrefill.url) setUrl(locatePrefill.url);
    setOpeningLine(locatePrefill.openingLine);
    setClosingLine(locatePrefill.closingLine);
    setLocateMode(true);
    setAutoScanMode(false);
    onConsumeLocatePrefill?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locatePrefill]);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.url) setUrl(prefill.url);
    if (prefill.from) setFrom(prefill.from);
    if (prefill.to) {
      setEndMode("end");
      setTo(prefill.to);
    } else if (typeof prefill.duration === "number") {
      setEndMode("duration");
      setDuration(String(prefill.duration));
    }
    if (prefill.startPhrase) setStartPhrase(prefill.startPhrase);
    if (prefill.endPhrase) setEndPhrase(prefill.endPhrase);
    if (prefill.host) setHost(prefill.host);
    if (prefill.guests && prefill.guests.length > 0) setGuests(prefill.guests.join(", "));
    if (prefill.topic) setTopic(prefill.topic);
    setPrefilledBanner(true);
    setFieldErrors({});
    onConsumePrefill?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Auto-dismiss the prefilled banner after a few seconds.
  useEffect(() => {
    if (!prefilledBanner) return;
    const t = window.setTimeout(() => setPrefilledBanner(false), 4500);
    return () => window.clearTimeout(t);
  }, [prefilledBanner]);

  // Load the music catalog once on mount + seed the form with a random
  // pick. Empty list → leave musicTrack="" (which the backend reads as
  // "let Claude pick"). Catalog fetch failure is non-fatal — the form
  // still works, the section just shows the error inline and falls back
  // to the Claude pick path.
  useEffect(() => {
    let cancelled = false;
    listMusicTracks()
      .then((tracks) => {
        if (cancelled) return;
        setMusicTracks(tracks);
        // Seed a random default ONLY on a fresh form. When a draft was
        // restored, the operator's saved pick (including "Let Claude pick"
        // = "") must win — don't clobber it with a new random roll.
        if (tracks.length > 0 && !hadDraft) {
          setMusicTrack(tracks[Math.floor(Math.random() * tracks.length)]!.filename);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setMusicError(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, [hadDraft]);

  // Autosave the in-progress form to localStorage on every change so leaving
  // the page (or refreshing) doesn't lose typed work; it's restored on the
  // next mount. Persist only when there's real content — an untouched form
  // clears the key instead of leaving an empty draft behind. Transient UI
  // (errors, banners, submit flags, the fetched music catalog) is NOT saved.
  useEffect(() => {
    const draft: NewJobDraft = {
      url, from, endMode, to, duration, startPhrase, endPhrase,
      host, guests, topic, autoCaption, autoSocial,
      autoScanMode, locateMode, openingLine, closingLine, scanCount, musicTrack,
    };
    if (draftHasContent(draft)) saveDraft(draft);
    else clearDraft();
  }, [
    url, from, endMode, to, duration, startPhrase, endPhrase,
    host, guests, topic, autoCaption, autoSocial,
    autoScanMode, locateMode, openingLine, closingLine, scanCount, musicTrack,
  ]);

  /** Re-roll the random default — useful when the operator doesn't
   *  love the first random pick and wants a fresh suggestion without
   *  scrolling the list. */
  const reshuffleMusic = (): void => {
    if (!musicTracks || musicTracks.length === 0) return;
    // Bias away from the currently-selected track when possible —
    // otherwise a 2-track catalog would re-roll into the same value
    // half the time and feel broken.
    const pool = musicTracks.length > 1
      ? musicTracks.filter((t) => t.filename !== musicTrack)
      : musicTracks;
    setMusicTrack(pool[Math.floor(Math.random() * pool.length)]!.filename);
  };

  // Button is enabled whenever there's something to submit and we're
  // not mid-submit. Format checks run on click via validateAll() —
  // disabling the button until the regex passes is too aggressive
  // (the user may be mid-typing) and hides the actual error.
  const canSubmit = submitting
    ? false
    : sourceMode === "upload"
      ? uploadFile !== null
      : url.trim() !== "" && from.trim() !== "";
  // Whether the form holds work worth clearing — drives the Clear button's
  // visibility (mirrors the autosave "has content" rule exactly).
  const hasContent = draftHasContent({ url, from, to, duration, startPhrase, endPhrase, host, guests, topic, openingLine, closingLine });

  const validateAll = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    // Upload mode has no url/from — its only requirement (a selected file)
    // is enforced in the handleSubmit upload branch. Skip the YouTube
    // field checks so they don't block submission.
    if (sourceMode === "upload") return errs;
    const urlErr = validateYouTubeUrl(url);
    if (urlErr) errs.url = urlErr;
    const fromErr = validateTimestamp(from, "Approx. start", { required: true });
    if (fromErr) errs.from = fromErr;
    if (endMode === "end" && to.trim()) {
      const toErr = validateTimestamp(to, "End");
      if (toErr) errs.to = toErr;
    }
    if (endMode === "duration" && duration.trim()) {
      const durErr = validateTimestamp(duration, "Duration");
      if (durErr) errs.duration = durErr;
    }
    return errs;
  };

  /**
   * Save the in-progress form to the Backlog so the operator can quit
   * the page and come back later without losing their work. Unlike
   * Start pipeline, the validation is relaxed — a backlog entry can
   * be partial (e.g. URL only, fill in timestamps later).
   */
  const handleSaveDraft = async (): Promise<void> => {
    if (submitting || savingDraft) return;
    setSavingDraft(true);
    setError(null);
    try {
      const guestList = guests.split(",").map((g) => g.trim()).filter(Boolean);
      const durationSec = endMode === "duration" ? parseDuration(duration) : NaN;
      const draft: BacklogDraft = {
        url: url.trim() || undefined,
        from: from.trim() || undefined,
        to: endMode === "end" ? (to.trim() || undefined) : undefined,
        duration: endMode === "duration" && Number.isFinite(durationSec) ? durationSec : undefined,
        startPhrase: startPhrase.trim() || undefined,
        endPhrase: endPhrase.trim() || undefined,
        host: host.trim() || undefined,
        guests: guestList.length ? guestList : undefined,
        topic: topic.trim() || undefined,
      };
      // The backlog stores at least one identifying field — if the
      // form is totally empty, save would still succeed and produce
      // a row of all-undefined which is useless.
      const hasAnything = Object.values(draft).some((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "",
      );
      if (!hasAnything) {
        setError("Fill in at least one field before saving as draft.");
        setSavingDraft(false);
        return;
      }
      await addBacklog(draft);
      clearDraft(); // saved to the backlog — drop the local autosave draft
      setSavedPulse(true);
      window.setTimeout(() => setSavedPulse(false), 2200);
      onSavedToBacklog?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (submitting) return;
    const errs = validateAll();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError(null);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Upload mode: stream the local file to /api/uploads, then create a
      // job referencing the returned uploadId. No url/from/to/phrases/pan —
      // the pipeline runs only the branding tail at verbatim length.
      if (sourceMode === "upload") {
        if (!uploadFile) {
          setError("Select a video file to upload.");
          setSubmitting(false);
          return;
        }
        const uploadGuests = guests.split(",").map((g) => g.trim()).filter(Boolean);
        setUploadPct(0);
        setUploadError(null);
        const { uploadId } = await uploadVideo(uploadFile, setUploadPct);
        const { id } = await createJob({
          localSource: uploadId,
          host: host.trim() || undefined,
          topic: topic.trim() || undefined,
          guests: uploadGuests.length ? uploadGuests : undefined,
          musicTrack: musicTrack.trim() || undefined,
        });
        clearDraft(); // job started — next visit to New Job starts fresh
        onCreated(id);
        return;
      }

      const guestList = guests.split(",").map((g) => g.trim()).filter(Boolean);
      // Only the active end-mode field is sent. The other stays in form
      // state if the user toggles back so they don't lose what they typed.
      const durationSec = endMode === "duration" ? parseDuration(duration) : NaN;
      const { id } = await createJob({
        url: url.trim(),
        from: from.trim(),
        to: endMode === "end" ? (to.trim() || undefined) : undefined,
        duration: endMode === "duration" && Number.isFinite(durationSec) ? durationSec : undefined,
        startPhrase: startPhrase.trim() || undefined,
        endPhrase: endPhrase.trim() || undefined,
        host: host.trim() || undefined,
        guests: guestList.length ? guestList : undefined,
        topic: topic.trim() || undefined,
        // Empty string → omit so the backend falls back to the Claude
        // pick (the "Let Claude pick" radio option).
        musicTrack: musicTrack.trim() || undefined,
      });
      clearDraft(); // job started — next visit to New Job starts fresh
      onCreated(id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "failed to create job";
      setError(msg);
      // In upload mode also surface the failure inline in the upload panel
      // (e.g. an upload that 413'd or hit a network error mid-transfer).
      if (sourceMode === "upload") setUploadError(msg);
      setSubmitting(false);
    }
  };

  const handleAutoScan = async (): Promise<void> => {
    const urlErr = validateYouTubeUrl(url);
    if (urlErr) {
      setFieldErrors({ url: urlErr });
      return;
    }
    setScanning(true);
    setScanError(null);
    try {
      const guestList = guests.split(",").map((s) => s.trim()).filter(Boolean);
      const { scanId } = await createScan(
        url.trim(), undefined, scanCount,
        musicTouched ? (musicTrack.trim() || undefined) : undefined,
        {
          // Operator's roster hints — an operator-typed host is authoritative and
          // gets locked onto every candidate (mirrors handleLocate). Absent →
          // each scene keeps the scan's title-biased guess (prior behavior).
          host: host.trim() || undefined,
          guests: guestList.length ? guestList : undefined,
          topic: topic.trim() || undefined,
        },
      );
      // channelSlug omitted — server resolves active channel (deliberate, safe omission).
      // Only a DELIBERATE pick carries onto candidates; an untouched form sends
      // nothing → each scene's pickMusic (Claude) chooses per scene, as before.
      // Hand the scanId to the Backlog so it can show live scan progress while
      // candidates stream in async server-side; operator lands on Backlog right away.
      clearDraft(); // scan submitted — clear the New Job draft
      onScanComplete?.(scanId);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleLocate = async (): Promise<void> => {
    const urlErr = validateYouTubeUrl(url);
    if (urlErr) { setFieldErrors({ url: urlErr }); return; }
    if (!openingLine.trim()) { setScanError("Add the opening line of the scene you want."); return; }
    setScanning(true);
    setScanError(null);
    try {
      const guestList = guests.split(",").map((s) => s.trim()).filter(Boolean);
      const { scanId } = await createLocate(url.trim(), openingLine.trim(), closingLine.trim() || undefined, {
        host: host.trim() || undefined,
        guests: guestList.length ? guestList : undefined,
        topic: topic.trim() || undefined,
        // Deliberate pick only — see handleScan. Untouched → Claude per-scene.
        musicTrack: musicTouched ? (musicTrack.trim() || undefined) : undefined,
      });
      // Reuse the same Backlog routing + live progress card as auto-scan.
      clearDraft(); // locate submitted — clear the New Job draft
      onScanComplete?.(scanId);
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'locate failed');
    } finally {
      setScanning(false);
    }
  };

  /** Reset every field to its fresh-form default and drop the saved draft.
   *  The autosave effect then sees an empty form and keeps the key cleared. */
  const handleClear = (): void => {
    setUrl(""); setFrom(""); setEndMode("end"); setTo(""); setDuration("");
    setStartPhrase(""); setEndPhrase(""); setHost(""); setGuests(""); setTopic("");
    setAutoCaption(true); setAutoSocial(true);
    setAutoScanMode(false); setLocateMode(false);
    setOpeningLine(""); setClosingLine(""); setScanCount(SCAN_DEFAULT_COUNT);
    // Re-roll a fresh random suggestion like a brand-new form would, or fall
    // back to "Let Claude pick" ("") when the catalog hasn't loaded yet.
    setMusicTrack(
      musicTracks && musicTracks.length > 0
        ? musicTracks[Math.floor(Math.random() * musicTracks.length)]!.filename
        : "",
    );
    setFieldErrors({});
    setError(null);
    setScanError(null);
    // Reset upload-mode transient state too (the file picker, progress, and
    // its inline error). sourceMode itself is left as-is — Clear resets the
    // form contents, not which front door the operator chose.
    setUploadFile(null);
    setUploadPct(0);
    setUploadError(null);
    clearDraft();
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto" }}>
      <PageHeader
        eyebrow="New job"
        title="Turn a YouTube moment into a publish-ready short."
        subtitle={
          sourceMode === "sample"
            ? `Pick a clip below and the full pipeline runs on it — 9:16, burned-in ${captionLanguage} captions, your brand, plus a ready-to-paste social caption.`
            : `Paste a URL, mark when the moment starts, and let the pipeline do the rest. You'll get a 9:16 video with burned-in ${captionLanguage} captions plus a ready-to-paste social caption.`
        }
      />

      {samplesOffNotice && (
        <div
          role="status"
          style={{
            margin: "0 0 16px",
            padding: "11px 14px",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,.10)",
            background: "rgba(255,255,255,.03)",
            fontSize: 13,
            color: "var(--ql-fg-muted)",
            lineHeight: 1.55,
          }}
        >
          We don't have any sample clips ready right now — paste a YouTube link instead, or upload a
          9:16 clip.
        </div>
      )}

      {prefilledBanner && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(74,222,128,.08)",
            border: "1px solid rgba(74,222,128,.30)",
            color: "var(--ql-success)",
            fontSize: 12,
            marginBottom: 14,
          }}
        >
          <Check size={14} strokeWidth={2.5} />
          <span>Pre-filled from backlog. Review the fields, then start the pipeline.</span>
        </div>
      )}

      <div style={{ marginBottom: 4 }}>
        <ToggleRow
          checked={sourceMode === "upload"}
          onChange={(v) => {
            setSourceMode(v ? "upload" : "youtube");
            setUploadFile(null);
            setUploadError(null);
            setUploadPct(0);
            // Leaving upload mode shouldn't strand a stale upload error in
            // the shared error slot.
            setError(null);
          }}
          Icon={Upload}
          title="Upload video"
          sub="Already have a 9:16 clip? Upload it and run only the branding pipeline — no download, cut, or crop."
        />
        {sourceMode === "upload" && (
          <div
            style={{
              marginTop: 4,
              padding: "12px 16px",
              borderRadius: 10,
              background: "rgba(255,255,255,.02)",
              border: "1px solid rgba(255,255,255,.06)",
            }}
          >
            <input
              type="file"
              accept="video/*,.mov,.mp4,.m4v,.webm,.mkv"
              id="upload-file-input"
              onChange={(e) => {
                setUploadFile(e.target.files?.[0] ?? null);
                setUploadError(null);
                setUploadPct(0);
              }}
              style={{ display: "none" }}
            />
            <label
              htmlFor="upload-file-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                setUploadFile(e.dataTransfer.files?.[0] ?? null);
                setUploadError(null);
                setUploadPct(0);
              }}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                gap: 12,
                padding: isMobile ? "16px 14px" : "18px 20px",
                borderRadius: 12,
                border: `1px dashed ${uploadFile ? "rgba(255,213,0,.45)" : "rgba(255,255,255,.18)"}`,
                background: uploadFile ? "rgba(255,213,0,.05)" : "rgba(255,255,255,.02)",
                color: uploadFile ? "var(--ql-fg)" : "var(--ql-fg-muted)",
                cursor: "pointer",
                textAlign: isMobile ? "center" : "left",
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: 10,
                  background: uploadFile ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
                  color: uploadFile ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Upload size={18} strokeWidth={1.75} />
              </span>
              <span style={{ minWidth: 0, fontSize: 13, fontWeight: 600, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                {uploadFile ? uploadFile.name : "Click or drag a video file here"}
              </span>
            </label>

            {uploadFile && uploadPct > 0 && uploadPct < 100 && (
              <div style={{ marginTop: 10 }}>
                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: "rgba(255,255,255,.08)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${uploadPct}%`,
                      height: "100%",
                      background: "var(--ql-yellow)",
                      transition: "width .15s var(--ql-ease)",
                    }}
                  />
                </div>
                <div style={{ marginTop: 6, fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)" }}>
                  Uploading… {uploadPct}%
                </div>
              </div>
            )}

            {uploadError && (
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--ql-danger)" }}>{uploadError}</div>
            )}
          </div>
        )}
      </div>

      {samplesOn && (
        <div style={{ marginBottom: 4 }} data-tour="new-sample">
          <ToggleRow
            checked={sourceMode === "sample"}
            onChange={(v) => {
              setSourceMode(v ? "sample" : "youtube");
              // Leaving upload mode behind shouldn't strand its transient
              // state (a picked file, a half-finished progress bar, an error).
              setUploadFile(null);
              setUploadError(null);
              setUploadPct(0);
              setError(null);
            }}
            Icon={Clapperboard}
            title="Try a sample"
            sub="No video yet? Render one of our clips in your brand — nothing to download, the real pipeline, a real result."
          />
          {sourceMode === "sample" && (
            <div style={{ marginTop: 10 }}>
              <SamplePicker
                onCreated={onCreated}
                intro={firstVideo ? "Pick a sample — we'll render it in your brand so you can see it live." : undefined}
              />
            </div>
          )}
        </div>
      )}

      {sourceMode === "youtube" && features?.autoScan && (
        <div style={{ marginBottom: 4 }}>
          <ToggleRow
            checked={autoScanMode}
            onChange={(v) => { setAutoScanMode(v); setScanError(null); if (v) setLocateMode(false); if (!v) setScanCount(SCAN_DEFAULT_COUNT); }}
            Icon={Search}
            title="Auto-scan whole video"
            sub={`Paste a URL — the AI finds up to ${scanCount} best moment${scanCount === 1 ? "" : "s"} for you. Candidates appear in the Backlog ready to render.`}
          />
          {autoScanMode && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 16px",
                marginTop: 4,
                borderRadius: 10,
                background: "rgba(255,255,255,.02)",
                border: "1px solid rgba(255,255,255,.06)",
              }}
            >
              <span style={{ ...fieldLabelStyle, marginBottom: 0, whiteSpace: "nowrap" }}>Candidates</span>
              <div style={{ display: "flex", gap: 4 }}>
                {SCAN_PRESET_COUNTS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setScanCount(n)}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "inherit",
                      border: scanCount === n ? "1px solid rgba(255,213,0,.50)" : "1px solid rgba(255,255,255,.10)",
                      cursor: "pointer",
                      background: scanCount === n ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
                      color: scanCount === n ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                      transition: "all .15s var(--ql-ease)",
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                max={SCAN_MAX_COUNT}
                value={scanCount}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(SCAN_MAX_COUNT, Number(e.target.value) || 1));
                  setScanCount(v);
                }}
                style={{
                  width: 60,
                  padding: "4px 8px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.10)",
                  background: "rgba(17,24,39,.5)",
                  color: "var(--ql-fg)",
                  fontSize: 13,
                  fontFamily: "var(--ql-font-mono)",
                  outline: "none",
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}
        </div>
      )}

      {sourceMode === "youtube" && features?.autoScan && (
        <div style={{ marginBottom: 4 }}>
          <ToggleRow
            checked={locateMode}
            onChange={(v) => { setLocateMode(v); setScanError(null); if (v) setAutoScanMode(false); }}
            Icon={Crosshair}
            title="Find a scene I know"
            sub="Saw the clip already? Paste the video URL below + the opening line you remember — the AI locates it, sets clean boundaries, and drops it in the Backlog ready to render."
          />
          {locateMode && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 14,
                padding: "12px 16px",
                marginTop: 4,
                borderRadius: 10,
                background: "rgba(255,255,255,.02)",
                border: "1px solid rgba(255,255,255,.06)",
              }}
            >
              <div>
                <div style={fieldLabelStyle}>
                  Opening line <span style={{ color: "var(--ql-yellow)" }}>*</span>
                </div>
                <input
                  type="text"
                  value={openingLine}
                  onChange={(e) => { setOpeningLine(e.target.value); setScanError(null); }}
                  placeholder={'e.g. "I think that’s my superpower"'}
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={fieldLabelStyle}>
                  Closing line <span style={{ color: "var(--ql-fg-subtle)" }}>(optional)</span>
                </div>
                <input
                  type="text"
                  value={closingLine}
                  onChange={(e) => setClosingLine(e.target.value)}
                  placeholder="blank = AI picks a clean end"
                  style={inputStyle}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {sourceMode === "youtube" && (
        <>
      <SectionLabel num="1" label="Source" />
      <div style={cardStyle}>
        <div style={fieldLabelStyle}>YouTube URL</div>
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ql-fg-muted)",
            }}
          >
            <Youtube size={18} strokeWidth={1.75} />
          </div>
          <input
            value={url}
            onChange={(e) => { setUrl(e.target.value); clearFieldError("url"); }}
            placeholder="https://www.youtube.com/watch?v=..."
            style={withError(
              { ...monoInputStyle, paddingLeft: 46, padding: "14px 16px 14px 46px", borderRadius: 14 },
              !!fieldErrors.url,
            )}
          />
        </div>
        {fieldErrors.url && <FieldError>{fieldErrors.url}</FieldError>}

        {!autoScanMode && !locateMode && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginTop: 18 }}>
              <div>
                <div style={fieldLabelStyle}>
                  Approx. start <span style={{ color: "var(--ql-yellow)" }}>*</span>
                </div>
                <input
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); clearFieldError("from"); }}
                  placeholder="12:43"
                  style={withError(monoInputStyle, !!fieldErrors.from)}
                />
                {fieldErrors.from && <FieldError>{fieldErrors.from}</FieldError>}
              </div>
              <div>
                <div style={{ ...fieldLabelStyle, display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <span>
                    {endMode === "end" ? "End" : "Duration"}{" "}
                    <span style={{ color: "var(--ql-fg-subtle)", fontWeight: 400 }}>(optional)</span>
                  </span>
                  <EndModeToggle mode={endMode} onChange={setEndMode} />
                </div>
                {endMode === "end" ? (
                  <input
                    key="end-input"
                    value={to}
                    onChange={(e) => { setTo(e.target.value); clearFieldError("to"); }}
                    placeholder="auto"
                    style={withError(monoInputStyle, !!fieldErrors.to)}
                  />
                ) : (
                  <input
                    key="duration-input"
                    value={duration}
                    onChange={(e) => { setDuration(e.target.value); clearFieldError("duration"); }}
                    placeholder='1:30 or 90'
                    style={withError(monoInputStyle, !!fieldErrors.duration)}
                  />
                )}
                {(fieldErrors.to || fieldErrors.duration) && (
                  <FieldError>{fieldErrors.to ?? fieldErrors.duration}</FieldError>
                )}
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={fieldLabelStyle}>
                First spoken words{" "}
                <span style={{ color: "var(--ql-fg-subtle)", fontWeight: 400 }}>(optional, recommended)</span>
              </div>
              <input
                value={startPhrase}
                onChange={(e) => setStartPhrase(e.target.value)}
                placeholder='e.g. "If you are awake"'
                style={inputStyle}
              />
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={fieldLabelStyle}>
                Last spoken words{" "}
                <span style={{ color: "var(--ql-fg-subtle)", fontWeight: 400 }}>(optional)</span>
              </div>
              <input
                value={endPhrase}
                onChange={(e) => setEndPhrase(e.target.value)}
                placeholder='e.g. "to themselves"'
                style={inputStyle}
              />
            </div>

            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 10,
                background: "rgba(255,213,0,.05)",
                border: "1px solid rgba(255,213,0,.15)",
                fontSize: 12,
                color: "var(--ql-fg-muted)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <span style={{ color: "var(--ql-yellow)", flexShrink: 0, marginTop: 1 }}>
                <Info size={14} strokeWidth={2} />
              </span>
              <span>
                The timestamp can be approximate. <strong>First spoken words</strong> snap the clip start to those exact words within ±15s of your approximate start. <strong>Last spoken words</strong> are authoritative — the pipeline scans the whole plausible clip range forward (up to the clip-length cap, or up to your typed duration + 15s) and locks the end there. Leave end + duration + last words blank to let the AI detect the natural end.
              </span>
            </div>
          </>
        )}
      </div>
        </>
      )}

      {/* Everything below belongs to the form the operator fills in. Sample
          mode has no form — the card IS the submit — so the details,
          treatment, music and footer sections sit out. */}
      {sourceMode !== "sample" && (
        <>
      <SectionLabel num="2" label="Speakers & topic" />
      <div style={cardStyle}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
          <div>
            <div style={fieldLabelStyle}>Host</div>
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="e.g. Lewis Howes"
              style={inputStyle}
            />
          </div>
          <div>
            <div style={fieldLabelStyle}>Guests (comma-separated)</div>
            <input
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              placeholder="e.g. Mel Robbins"
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={fieldLabelStyle}>Topic / hook</div>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. discipline beats motivation"
            style={inputStyle}
          />
        </div>
      </div>

      <SectionLabel num="3" label="Treatment" />
      <div style={cardStyle}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <ToggleRow
            checked={autoCaption}
            onChange={setAutoCaption}
            Icon={Type}
            title={`Burn-in ${captionLanguage} captions`}
            sub="One-liner vs two-liner per cue, gold-on-cream emphasis, brand fonts, ASCII Latin digits."
          />
          <ToggleRow
            checked={autoSocial}
            onChange={setAutoSocial}
            Icon={MessageSquareQuote}
            title="Generate social caption"
            sub="Brand-voiced post text with hook + emoji, using your captions-templates few-shot examples."
          />
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: "var(--ql-fg-muted)" }}>
          Both treatments are part of the standard pipeline — toggles are visual hints for now. The render always
          produces both the .mp4 and the social caption.
        </div>
      </div>

      <SectionLabel num="4" label="Music" />
      <div style={cardStyle}>
        <MusicPicker
          tracks={musicTracks}
          selected={musicTrack}
          onChange={(t) => { setMusicTrack(t); setMusicTouched(true); }}
          onTracksChange={setMusicTracks}
          onReshuffle={reshuffleMusic}
          loadError={musicError}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          marginTop: 28,
          padding: isMobile ? "16px" : "20px 24px",
          borderRadius: 18,
          background: "rgba(16,18,27,.72)",
          border: "1px solid rgba(255,255,255,.06)",
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 11,
              color: "var(--ql-fg-muted)",
              marginBottom: 4,
              textTransform: "uppercase",
              letterSpacing: ".08em",
              fontWeight: 700,
            }}
          >
            Pipeline
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ql-gray-300)",
              fontFamily: "var(--ql-font-mono)",
            }}
          >
            {sourceMode === "upload"
              ? "prepare the video · write down what is said · captions · build the vertical video · post caption"
              : "get the video · write down what is said · find where the thought ends · captions · build the vertical video · post caption"}
          </div>
          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--ql-danger)" }}>
              {error}
            </div>
          )}
          {scanError && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--ql-danger)" }}>{scanError}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: isMobile ? "wrap" : "nowrap", justifyContent: isMobile ? "flex-end" : undefined }}>
          {hasContent && !submitting && !scanning && !savingDraft && (
            <button
              type="button"
              onClick={handleClear}
              title="Clear the form and discard the saved draft"
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "11px 14px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,.08)",
                background: "transparent",
                color: "var(--ql-fg-muted)",
                fontSize: 13, fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .15s var(--ql-ease)",
                whiteSpace: "nowrap",
              }}
            >
              <Eraser size={14} strokeWidth={2} />
              Clear
            </button>
          )}
          {sourceMode !== "upload" && !autoScanMode && !locateMode && (
            <button
              type="button"
              onClick={() => void handleSaveDraft()}
              disabled={savingDraft || submitting}
              title="Save the current form to your Backlog so you can come back later"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "11px 16px",
                borderRadius: 12,
                border: `1px solid ${savedPulse ? "rgba(74,222,128,.40)" : "rgba(255,255,255,.12)"}`,
                background: savedPulse ? "rgba(74,222,128,.10)" : "rgba(255,255,255,.04)",
                color: savedPulse ? "var(--ql-success)" : "var(--ql-fg)",
                fontSize: 13, fontWeight: 600,
                cursor: savingDraft ? "wait" : "pointer",
                fontFamily: "inherit",
                transition: "all .15s var(--ql-ease)",
                whiteSpace: "nowrap",
                opacity: submitting ? 0.5 : 1,
              }}
            >
              {savingDraft ? (
                <Loader2 size={14} className="ql-spin" />
              ) : savedPulse ? (
                <Check size={14} strokeWidth={2.4} />
              ) : (
                <BookmarkPlus size={14} strokeWidth={2} />
              )}
              {savingDraft ? "Saving…" : savedPulse ? "Saved to backlog" : "Save as draft"}
            </button>
          )}
          {features?.autoScan && locateMode ? (
            <button
              disabled={!url.trim() || !openingLine.trim() || scanning}
              onClick={() => void handleLocate()}
              style={ctaBtnStyle(!url.trim() || !openingLine.trim() || scanning ? false : true)}
            >
              {scanning ? (
                <><Loader2 size={16} className="ql-spin" /> Finding…</>
              ) : (
                <><Crosshair size={16} strokeWidth={2} /> Find scene</>
              )}
            </button>
          ) : features?.autoScan && autoScanMode ? (
            <button
              disabled={!url.trim() || scanning}
              onClick={() => void handleAutoScan()}
              style={ctaBtnStyle(!url.trim() || scanning ? false : true)}
            >
              {scanning ? (
                <><Loader2 size={16} className="ql-spin" /> Scanning…</>
              ) : (
                <><Search size={16} strokeWidth={2} /> Auto-scan</>
              )}
            </button>
          ) : (
            <button disabled={!canSubmit} onClick={handleSubmit} style={ctaBtnStyle(canSubmit)} data-tour="new-start">
              {submitting ? (
                <>
                  <Loader2 size={16} className="ql-spin" /> Starting…
                </>
              ) : (
                <>
                  Start pipeline <ArrowRight size={16} strokeWidth={2} />
                </>
              )}
            </button>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
}

/**
 * Tiny segmented control to choose between giving an end timestamp
 * versus a duration. Lives next to the field label so it doesn't add a
 * full extra row to the form. Pressing either pill swaps the input
 * underneath without dropping what the user typed in the other mode.
 */
function EndModeToggle({
  mode,
  onChange,
}: {
  mode: EndMode;
  onChange: (m: EndMode) => void;
}): React.JSX.Element {
  const opts: Array<{ id: EndMode; label: string }> = [
    { id: "end", label: "End" },
    { id: "duration", label: "Duration" },
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 2,
        borderRadius: 999,
        background: "rgba(255,255,255,.04)",
        border: "1px solid rgba(255,255,255,.06)",
        textTransform: "none",
        letterSpacing: "normal",
      }}
    >
      {opts.map((o) => {
        const active = mode === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            style={{
              padding: "4px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "inherit",
              border: "none",
              cursor: "pointer",
              background: active ? "linear-gradient(135deg,#FFD500,#FFA500)" : "transparent",
              color: active ? "#000" : "var(--ql-fg-muted)",
              transition: "all .15s var(--ql-ease)",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FieldError({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 11,
        color: "var(--ql-danger)",
        fontFamily: "var(--ql-font-mono)",
      }}
    >
      {children}
    </div>
  );
}

/** Tint an input red when it has a validation error. Keeps the rest
 * of the style intact so we don't have to fork inputStyle / monoInputStyle. */
function withError(base: CSSProperties, hasError: boolean): CSSProperties {
  if (!hasError) return base;
  return {
    ...base,
    borderColor: "var(--ql-danger)",
    boxShadow: "0 0 0 1px rgba(248,113,113,.30) inset",
  };
}

function SectionLabel({ num, label }: { num: string; label: string }): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 28, marginBottom: 12 }}>
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 800,
          background: "rgba(255,213,0,.14)",
          color: "var(--ql-yellow)",
          border: "1px solid rgba(255,213,0,.30)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {num}
      </span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--ql-yellow)",
          textTransform: "uppercase",
          letterSpacing: ".10em",
        }}
      >
        {label}
      </span>
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  Icon: typeof Type;
  title: string;
  sub: string;
}

function ToggleRow({ checked, onChange, Icon, title, sub }: ToggleProps): React.JSX.Element {
  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 16px",
    borderRadius: 14,
    background: checked ? "rgba(255,213,0,.04)" : "rgba(255,255,255,.02)",
    border: checked ? "1px solid rgba(255,213,0,.20)" : "1px solid rgba(255,255,255,.06)",
    cursor: "pointer",
  };
  return (
    <label style={rowStyle}>
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: checked ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
          color: checked ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={18} strokeWidth={1.75} />
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 2 }}>{sub}</div>
      </div>
      <div
        style={{
          width: 40,
          height: 22,
          borderRadius: 999,
          position: "relative",
          background: checked ? "linear-gradient(90deg,#FFD500,#FFA500)" : "rgba(255,255,255,.10)",
          transition: "background .2s var(--ql-ease)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 20 : 2,
            width: 18,
            height: 18,
            borderRadius: 999,
            background: "#fff",
            transition: "left .2s var(--ql-ease)",
            boxShadow: "0 2px 4px rgba(0,0,0,.3)",
          }}
        />
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ display: "none" }}
      />
    </label>
  );
}

